import { createFrotaDisponibilidade as createDisponibilidade, getVeiculos, getModelos } from "@spark/services/frotas";
import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { Form } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import { formStructure, formSections, validateForm } from './formConfig';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { searchMotoristaOptions, searchGestorOptions } from '@spark/utils/functions/excelLoader';
import { updateMultipleFieldsByName } from '@spark/ui-components/utils/formHelpers';

const mapVeiculoToOption = (veiculo = {}) => {
  const id = veiculo?.Id ?? veiculo?.id ?? veiculo?.IdVeiculo ?? veiculo?.idVeiculo;
  if (id === undefined || id === null || id === '') {
    return null;
  }

  const frotaNumero = veiculo?.Frota ?? veiculo?.frota ?? '';
  const placa = veiculo?.Placa ?? veiculo?.placa ?? '';
  const modeloDescricao = veiculo?.ModeloVeicular?.Descricao ?? veiculo?.ModeloVeicular?.descricao ?? veiculo?.ModeloVeicular?.NomeTipoModeloVeicular ?? veiculo?.modeloVeicular?.Descricao ?? veiculo?.modeloVeicular?.descricao ?? '';
  const labelParts = [frotaNumero, placa, modeloDescricao].filter(Boolean);
  const label = labelParts.length ? labelParts.join(' - ') : `Veiculo ${id}`;

  return {
    value: String(id),
    label,
    data: veiculo
  };
};

const extractVeiculoSelection = selected => {
  if (!selected) {
    return {
      idVeiculo: null,
      frotaDescricao: '',
      placa: '',
      label: ''
    };
  }

  if (typeof selected === 'object') {
    const rawId = selected.value ?? selected.id ?? selected.Id ?? selected.data?.Id ?? selected.data?.id ?? selected.data?.IdVeiculo ?? selected.data?.idVeiculo ?? null;
    const hasRawId = rawId !== null && rawId !== undefined && rawId !== '';
    const parsedId = hasRawId ? Number(rawId) : null;
    const idVeiculo = !hasRawId ? null : Number.isNaN(parsedId) ? rawId : parsedId;
    const data = selected.data ?? {};
    const frotaDescricao = data.Frota ?? data.frota ?? selected.label ?? '';
    const placa = data.Placa ?? data.placa ?? '';
    const modelo = data.ModeloVeicular ?? data.modeloVeicular ?? null;
    const modeloDescricao = modelo?.Descricao ?? modelo?.descricao ?? '';
    const computedLabel = selected.label || [frotaDescricao, placa].filter(Boolean).join(' - ') || (idVeiculo !== null ? `Veiculo ${idVeiculo}` : '');
    return {
      idVeiculo,
      frotaDescricao,
      placa,
      modeloDescricao,
      label: computedLabel
    };
  }

  const label = String(selected || '');
  const hasRawId = selected !== null && selected !== undefined && selected !== '';
  const parsedId = hasRawId ? Number(selected) : null;
  const idVeiculo = !hasRawId ? null : Number.isNaN(parsedId) ? selected : parsedId;

  return {
    idVeiculo,
    frotaDescricao: label,
    placa: '',
    modeloDescricao: '',
    label
  };
};

const mapModelosToOptions = (modelos = []) => {
  return modelos.map(modelo => {
    const id = modelo?.Id ?? modelo?.id ?? modelo?.IdModeloVeicular ?? modelo?.idModeloVeicular ?? null;
    if (id === null || id === undefined || id === '') {
      return null;
    }

    const rawLabel = modelo?.Descricao ?? modelo?.descricao ?? modelo?.Nome ?? modelo?.nome ?? modelo?.NomeTipoModeloVeicular ?? modelo?.nomeTipoModeloVeicular ?? `Modelo ${id}`;
    const label = String(rawLabel || '').trim() || `Modelo ${id}`;

    return {
      value: String(id),
      label,
      data: modelo
    };
  }).filter(Boolean);
};

const createModeloOptionFromVeiculo = (veiculo = {}) => {
  const modelo = veiculo?.ModeloVeicular ?? veiculo?.modeloVeicular ?? {};
  const id = veiculo?.IdModeloVeicular ?? veiculo?.idModeloVeicular ?? modelo?.Id ?? modelo?.id ?? modelo?.IdModeloVeicular ?? modelo?.idModeloVeicular ?? null;

  if (id === null || id === undefined || id === '') {
    return null;
  }

  const rawLabel = modelo?.Descricao ?? modelo?.descricao ?? modelo?.Nome ?? modelo?.nome ?? modelo?.NomeTipoModeloVeicular ?? modelo?.nomeTipoModeloVeicular ?? veiculo?.Descricao ?? veiculo?.descricao ?? `Modelo ${id}`;
  const label = String(rawLabel || '').trim() || `Modelo ${id}`;

  return {
    value: String(id),
    label,
    data: modelo && Object.keys(modelo).length ? modelo : veiculo
  };
};

const mapFormToCreateApi = values => {
  const frotaSelecionada = extractVeiculoSelection(values.frota);
  const frotaId = frotaSelecionada.idVeiculo ?? null;
  const frotaDescricao = frotaSelecionada.frotaDescricao || frotaSelecionada.label;
  const tipoModeloField = values.tipoModelo;
  const tipoModeloValue = typeof tipoModeloField === 'object' ? tipoModeloField?.value : tipoModeloField;
  const tipoModeloLabel = typeof tipoModeloField === 'object' ? tipoModeloField?.label : tipoModeloField;
  const parsedModeloId = (() => {
    if (tipoModeloValue === null || tipoModeloValue === undefined || tipoModeloValue === '') {
      return null;
    }

    if (typeof tipoModeloValue === 'number' && Number.isFinite(tipoModeloValue)) {
      return tipoModeloValue;
    }

    const normalized = typeof tipoModeloValue === 'string' ? tipoModeloValue.trim() : tipoModeloValue;
    if (normalized === '') {
      return null;
    }

    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? null : parsed;
  })();
  const resolvedTipoModelo = String(tipoModeloLabel || frotaSelecionada.modeloDescricao || '');
  const frotaValor = frotaId ?? (frotaDescricao ? frotaDescricao : null);
  return {
    DataDisponibilidade: values.DataDisponibilidade ? new Date(values.DataDisponibilidade).toISOString().slice(0, 19).replace('T', ' ') : null,
    LocalCidade: values.vazioem || '',
    LocalEstado: values.uf || '',
    IdVeiculo: frotaId,
    Frota: frotaValor,
    Veiculo: frotaSelecionada.placa || '',
    TipoModelo: resolvedTipoModelo,
    IdModeloVeicular: parsedModeloId,
    NomeMotorista: typeof values.motorista === 'object' ? values.motorista?.label : values.motorista,
    NomeGestor: typeof values.gestor === 'object' ? values.gestor?.label : values.gestor,
    Vazio: values.vazio || false,
    Coleta: values.coleta || false,
    Negociacao: values.negociacao || false,
    DescargaPendente: values.descargaPendente || false,
    NomeComercial: values.responsavelComercial || '',
    Destino: values.destino || '',
    DiasParado: values.diasParados || 0,
    DataAgendamento: values.dataAgendamento ? new Date(values.dataAgendamento).toISOString().slice(0, 19).replace('T', ' ') : null,
    DiasAguardandoDescarga: values.diasAguardandoDescarga || 0
  };
}; // Calcular dias aguardando descarga com base na DataAgendamento da API


const calcularDiasAguardandoDescarga = dataAgendamento => {
  if (!dataAgendamento) return 0;
  const dataAtual = new Date();
  const dataAgendamentoObj = new Date(dataAgendamento);
  const diferencaMs = dataAtual.getTime() - dataAgendamentoObj.getTime();
  return Math.floor(diferencaMs / (1000 * 60 * 60 * 24));
};

const Forms = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);
  const shouldSkipNextModeloChange = useRef(false);
  const shouldSkipNextFrotaChange = useRef(false);
  const [isFrotaDisabled, setIsFrotaDisabled] = useState(false);
  const [modelosOptions, setModelosOptions] = useState([]);
  const [loadingModelos, setLoadingModelos] = useState(false);

  const mergeModeloOptions = useCallback((optionsToAdd = []) => {
    if (!Array.isArray(optionsToAdd) || optionsToAdd.length === 0) {
      return;
    }

    setModelosOptions(prevOptions => {
      const optionsMap = new Map(prevOptions.map(option => [option.value, option]));
      optionsToAdd.forEach(option => {
        if (option && option.value !== undefined && option.value !== null && option.value !== '') {
          optionsMap.set(String(option.value), option);
        }
      });
      const toComparableLabel = option => (option?.label ? option.label.toString() : '');
      return Array.from(optionsMap.values()).sort((a, b) => toComparableLabel(a).localeCompare(toComparableLabel(b), 'pt-BR'));
    });
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchInitialModelos = async () => {
      try {
        setLoadingModelos(true);
        const response = await getModelos({
          limit: 50
        });
        if (!isMounted) {
          return;
        }

        const registrosResponse = response?.registros ?? response?.Registros ?? response?.data?.registros ?? response?.data?.Registros ?? response?.modelos ?? response?.Modelos ?? [];
        const registros = Array.isArray(registrosResponse) ? registrosResponse : [];
        const options = mapModelosToOptions(registros);

        if (options.length > 0) {
          mergeModeloOptions(options);
        } else {
          setModelosOptions([]);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Erro ao carregar modelos de veiculos:', error);
          setModelosOptions([]);
        }
      } finally {
        if (isMounted) {
          setLoadingModelos(false);
        }
      }
    };

    fetchInitialModelos();

    return () => {
      isMounted = false;
    };
  }, [mergeModeloOptions]);
  const formatVeiculoOption = useCallback(mapVeiculoToOption, []);
  const handleSearchFrotas = useCallback(async (searchTerm = '') => {
    const termo = String(searchTerm || '').trim();
    if (!termo) return [];

    try {
      const response = await getVeiculos({
        frota: termo,
        quantidadePorPagina: 50,
        pagina: 1,
        limit: 50
      });

      const registrosResponse = response?.registros ?? response?.Registros ?? response?.data?.registros ?? response?.data?.Registros ?? response?.veiculos ?? response?.Veiculos ?? [];
      const registros = Array.isArray(registrosResponse) ? registrosResponse : [];
      const options = registros.map(formatVeiculoOption).filter(Boolean);

      const unique = [];
      const seen = new Set();
      options.forEach(option => {
        if (option && !seen.has(option.value)) {
          seen.add(option.value);
          unique.push(option);
        }
      });

      return unique;
    } catch (error) {
      console.error('Erro ao buscar frotas:', error);
      return [];
    }
  }, [formatVeiculoOption]);
  const handleSearchModelos = useCallback(async (searchTerm = '') => {
    const termo = String(searchTerm || '').trim();
    if (!termo || termo.length < 2) {
      return [];
    }

    try {
      const response = await getModelos({
        limit: 50,
        nomeTipoModeloVeicular: termo
      });

      const registrosResponse = response?.registros ?? response?.Registros ?? response?.data?.registros ?? response?.data?.Registros ?? response?.modelos ?? response?.Modelos ?? [];
      const registros = Array.isArray(registrosResponse) ? registrosResponse : [];
      const options = mapModelosToOptions(registros);

      if (options.length > 0) {
        mergeModeloOptions(options);
      }

      return options;
    } catch (error) {
      console.error('Erro ao buscar modelos de veiculos:', error);
      return [];
    }
  }, [mergeModeloOptions]);
  const handleFrotaSelect = useCallback(selectedOption => {
    if (shouldSkipNextFrotaChange.current) {
      shouldSkipNextFrotaChange.current = false;
      return;
    }

    const currentForm = formRef.current;
    if (!currentForm) return;

    const normalizeTipoModeloValue = value => {
      if (value === null || value === undefined) {
        return {
          hasValue: false,
          value: null,
          label: ''
        };
      }

      if (typeof value === 'object') {
        const normalizedValue = value?.value ?? value?.Value ?? value?.id ?? value?.Id ?? null;
        const normalizedLabel = value?.label ?? value?.Label ?? value?.Descricao ?? value?.descricao ?? '';
        return {
          hasValue: normalizedValue !== null && normalizedValue !== undefined,
          value: normalizedValue !== null && normalizedValue !== undefined ? String(normalizedValue) : null,
          label: String(normalizedLabel ?? '')
        };
      }

      return {
        hasValue: false,
        value: null,
        label: String(value)
      };
    };

    const tipoModelosSaoIguais = (atual, proximo) => {
      const normalizadoAtual = normalizeTipoModeloValue(atual);
      const normalizadoProximo = normalizeTipoModeloValue(proximo);

      if (normalizadoAtual.hasValue || normalizadoProximo.hasValue) {
        return normalizadoAtual.value === normalizadoProximo.value;
      }

      return normalizadoAtual.label === normalizadoProximo.label;
    };

    const applyTipoModeloChange = nextValue => {
      const state = typeof currentForm.getState === 'function' ? currentForm.getState() : null;
      const currentValue = state?.values?.tipoModelo ?? null;

      if (tipoModelosSaoIguais(currentValue, nextValue)) {
        shouldSkipNextModeloChange.current = false;
        return;
      }

      shouldSkipNextModeloChange.current = true;
      currentForm.change('tipoModelo', nextValue);
    };

    setIsFrotaDisabled(false);

    if (!selectedOption) {
      applyTipoModeloChange(null);
      return;
    }

    const data = selectedOption.data ?? {};
    const modeloOption = createModeloOptionFromVeiculo(data);

    if (modeloOption) {
      mergeModeloOptions([modeloOption]);
      applyTipoModeloChange(modeloOption);
      return;
    }

    const modelo = data.ModeloVeicular ?? data.modeloVeicular ?? null;
    const descricao = modelo?.Descricao ?? modelo?.descricao ?? modelo?.NomeTipoModeloVeicular ?? '';
    const fallbackDescricao = data?.Descricao ?? data?.NomeTipoModeloVeicular ?? '';
    const labelDescricao = typeof selectedOption?.label === 'string' ? selectedOption.label.split(' - ').pop()?.trim() : '';
    const resolvedDescricao = (descricao || fallbackDescricao || labelDescricao || '').trim();

    if (resolvedDescricao) {
      applyTipoModeloChange({
        value: resolvedDescricao,
        label: resolvedDescricao
      });
    } else {
      applyTipoModeloChange(null);
    }
  }, [mergeModeloOptions, setIsFrotaDisabled]);

  const handleModeloSelect = useCallback(selectedOption => {
    const currentForm = formRef.current;
    if (!currentForm) return;

    if (shouldSkipNextModeloChange.current) {
      shouldSkipNextModeloChange.current = false;
      return;
    }

    if (!selectedOption) {
      setIsFrotaDisabled(false);
      return;
    }

    setIsFrotaDisabled(true);
    if (selectedOption) {
      mergeModeloOptions([selectedOption]);
    }
    const state = typeof currentForm.getState === 'function' ? currentForm.getState() : null;
    const currentFrotaValue = state?.values?.frota ?? null;
    const isFrotaVazia = currentFrotaValue === null || currentFrotaValue === undefined || currentFrotaValue === '';
    if (isFrotaVazia) {
      shouldSkipNextFrotaChange.current = false;
    } else {
      shouldSkipNextFrotaChange.current = true;
      currentForm.change('frota', null);
    }
  }, [setIsFrotaDisabled, mergeModeloOptions]);

  const onSubmit = useCallback(async values => {
    try {
      setLoading(true);
      const payload = mapFormToCreateApi(values);
      await createDisponibilidade(payload);
      history.push('/pages/programacao/disponibilidade');
    } catch (err) {
      alert(`Erro ao criar disponibilidade: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [history]);
  const initialValues = useMemo(() => ({
    frota: null,
    tipoModelo: null,
    motorista: '',
    segmento: '',
    destino: '',
    responsavelComercial: '',
    uf: '',
    diasParados: 0,
    diasAguardandoDescarga: 0,
    dataAgendamento: null,
    DataDisponibilidade: null,
    gestor: '',
    vazioem: '',
    descargaPendente: false,
    vazio: false,
    coleta: false,
    negociacao: false
  }), []); // Atualiza a estrutura do formulário com as funções de busca

  const modeloSelectOptions = useMemo(() => {
    const placeholderOption = {
      value: '',
      label: 'Selecione o modelo'
    };
    const optionsMap = new Map();

    modelosOptions.forEach(option => {
      if (!option || option.value === undefined || option.value === null || option.value === '') {
        return;
      }
      optionsMap.set(String(option.value), option);
    });

    return [placeholderOption, ...Array.from(optionsMap.values())];
  }, [modelosOptions]);

  const updatedFormStructure = useMemo(() => {
    const fieldUpdates = {
      frota: {
        onSearch: handleSearchFrotas,
        customOnChange: handleFrotaSelect,
        maxResults: 50,
        disabled: isFrotaDisabled,
      },
      motorista: {
        onSearch: searchMotoristaOptions
      },
      gestor: {
        onSearch: searchGestorOptions
      },
      tipoModelo: {
        onSearch: handleSearchModelos,
        options: modeloSelectOptions,
        isClearable: true,
        disabled: loadingModelos,
        customOnChange: handleModeloSelect
      }
    };
    return updateMultipleFieldsByName(formStructure, fieldUpdates);
  }, [handleSearchFrotas, handleFrotaSelect, handleSearchModelos, modeloSelectOptions, loadingModelos, isFrotaDisabled, handleModeloSelect]);
  return <Form onSubmit={onSubmit} initialValues={initialValues} validate={validateForm}>
      {({
      handleSubmit,
      form,
      values
    }) => {
      const {
        reset
      } = form;
      formRef.current = form;
      return <Card>
          <CardBody>
            <FormContainer onSubmit={handleSubmit}>
              <DynamicForm structure={updatedFormStructure} sections={formSections(initialValues)} />
                
              <FormButtonToolbar className="mt-5">
                <ButtonWithLoading variant="primary" type="submit" loading={loading ? 'true' : undefined}>
                    
                  Salvar
                </ButtonWithLoading>
                <Button variant="secondary" type="button" onClick={() => history.goBack()}>
                  Cancelar
                </Button>
              </FormButtonToolbar>
            </FormContainer>
          </CardBody>
        </Card>;
    }}
    </Form>;
};

export default Forms;

